import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Target } from 'lucide-react';

export default function AuthPage({ setUser }) {
  const [isFirstUser, setIsFirstUser] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [checkingFirstUser, setCheckingFirstUser] = useState(true);

  const checkFirstUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/status`);
      const isOpen = response.data.registration_open;
      const userCount = response.data.user_count;
      
      // Show registration if no users OR if no admins exist
      setIsFirstUser(isOpen || userCount === 0);
      setShowRegister(isOpen || userCount === 0);
    } catch (error) {
      // If we can't check status, show the option to register anyway
      setIsFirstUser(true);
      setShowRegister(true);
    } finally {
      setCheckingFirstUser(false);
    }
  }, []);

  useEffect(() => {
    checkFirstUser();
  }, [checkFirstUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (showRegister) {
        // Try the setup-first-admin endpoint first, then fall back to register
        try {
          const response = await axios.post(`${API}/auth/setup-first-admin`, formData);
          localStorage.setItem('token', response.data.token);
          setUser(response.data.user);
          toast.success('Welcome! You are now the first admin.');
          return;
        } catch (setupError) {
          // If setup-first-admin fails, try normal register
          if (setupError.response?.status !== 403) {
            throw setupError;
          }
          // If 403, an admin exists, try normal register
          const response = await axios.post(`${API}/auth/register`, formData);
          localStorage.setItem('token', response.data.token);
          setUser(response.data.user);
          toast.success('Welcome! You are now the first admin.');
        }
      } else {
        // Login
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        toast.success('Welcome back!');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Authentication failed';
      
      // If registration fails with 403, it means users already exist
      if (showRegister && error.response?.status === 403) {
        toast.error('Registration is closed. Please login or contact an admin.');
        setShowRegister(false);
        setIsFirstUser(false);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Hero Section */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1761144530756-47ecd564f8ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxzcG9ydHMlMjBzaG9vdGluZyUyMGNvbXBldGl0aW9uJTIwdGFyZ2V0fGVufDB8fHx8MTc3MTg1NDA3MHww&ixlib=rb-4.1.0&q=85)' }}
      >
        <div className="absolute inset-0 bg-slate-900/70"></div>
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="flex items-center gap-4 mb-6">
            <img src="/logo.jpg" alt="Los Pistoleros" className="w-16 h-16 rounded-full" />
            <h1 className="font-barlow font-bold text-5xl tracking-tight">LOS PISTOLEROS</h1>
          </div>
          <p className="text-lg font-inter text-stone-200 max-w-md">
            Track competition performance. Upload scorecards. Dominate the standings.
          </p>
        </div>
      </div>

      {/* Auth Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src="/logo.jpg" alt="Los Pistoleros" className="w-12 h-12 rounded-full" />
            <h1 className="font-barlow font-bold text-3xl tracking-tight text-slate-900">LOS PISTOLEROS</h1>
          </div>

          <div className="bg-white border border-stone-200 rounded-sm p-8 shadow-sm">
            <h2 className="font-barlow font-bold text-2xl text-slate-900 mb-2 uppercase tracking-tight">
              {showRegister ? (isFirstUser ? 'First Time Setup' : 'Create First Admin') : 'Sign In'}
            </h2>
            <p className="text-stone-600 font-inter text-sm mb-6">
              {showRegister 
                ? (isFirstUser 
                    ? 'Welcome! Create the first administrator account to get started.' 
                    : 'Setup the first administrator account')
                : 'Enter your credentials to access Los Pistoleros'
              }
            </p>

            {isFirstUser && showRegister && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-6">
                <p className="text-amber-800 font-inter text-sm">
                  <strong>No users detected.</strong> The first account you create will have full admin privileges.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {showRegister && (
                <div>
                  <Label htmlFor="name" className="font-inter font-medium text-slate-900">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="mt-1 bg-white border-stone-300 rounded-sm focus:ring-2 focus:ring-amber-600 focus:border-transparent h-12 font-inter"
                    required={showRegister}
                    placeholder="As it appears on scorecards"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email" className="font-inter font-medium text-slate-900">Email</Label>
                <Input
                  id="email"
                  data-testid="auth-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="mt-1 bg-white border-stone-300 rounded-sm focus:ring-2 focus:ring-amber-600 focus:border-transparent h-12 font-inter"
                  required
                />
              </div>

              <div>
                <Label htmlFor="password" className="font-inter font-medium text-slate-900">Password</Label>
                <Input
                  id="password"
                  data-testid="auth-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="mt-1 bg-white border-stone-300 rounded-sm focus:ring-2 focus:ring-amber-600 focus:border-transparent h-12 font-inter"
                  required
                />
              </div>

              <Button
                type="submit"
                data-testid="auth-submit-button"
                disabled={loading || checkingFirstUser}
                className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm uppercase tracking-wider font-bold h-12 font-barlow transition-all active:scale-95"
              >
                {loading ? 'Processing...' : (showRegister ? 'Create Admin Account' : 'Sign In')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              {!showRegister && !checkingFirstUser && (
                <>
                  <p className="text-stone-600 font-inter text-sm mb-2">
                    Don't have an account? Contact an administrator.
                  </p>
                  <button
                    onClick={() => setShowRegister(true)}
                    className="text-amber-600 hover:text-amber-700 font-inter text-xs font-medium"
                  >
                    First time setup? Create admin account
                  </button>
                </>
              )}
              {showRegister && (
                <button
                  onClick={() => setShowRegister(false)}
                  className="text-amber-600 hover:text-amber-700 font-inter text-sm font-medium"
                >
                  Already have an account? Sign in
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}